import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, QrCode, LineChart, LifeBuoy, Award, ArrowRight, TrendingUp, Sparkles,
  Star, Users, Scale, CheckCircle2, Store, UtensilsCrossed, ChevronDown,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { usePageMeta } from "../lib/seo";
import { SiteHeader, SiteFooter } from "../components/SiteChrome";

// Research-backed proof points (Qualtrics XM Institute, Bain & Company, PwC)
const PROOF = [
  { stat: "87%", body: "of customers who have a very good experience are likely to repurchase — versus just 18% after a very poor one.", src: "Qualtrics XM Institute" },
  { stat: "4–8%", body: "above-market revenue growth for companies that lead on customer experience.", src: "Bain & Company" },
  { stat: "25–95%", body: "profit uplift from only a 5% increase in customer retention.", src: "Bain & Company" },
  { stat: "16%", body: "price premium customers will pay for a superior, welcoming service experience.", src: "PwC" },
];

const FEATURES = [
  { icon: Star, title: "Verified service identity", body: "A QR/NFC lanyard gives every frontline employee a trusted, revocable service identity — the person is the brand at the point of truth." },
  { icon: LineChart, title: "Servantage Service Index", body: "A composite, confidence-adjusted score (quality × confidence × recency × trust) — not a raw star average that small samples can distort." },
  { icon: LifeBuoy, title: "Close the loop", body: "Positive moments become recognition and reviews; negative ones open a service-recovery case instead of a public blow-up." },
  { icon: ShieldCheck, title: "Trusted by design", body: "Duplicate, velocity and cluster detection keep scores credible. Nothing is ever silently deleted, and employees can dispute." },
  { icon: Award, title: "Recognition, not punishment", body: "Build a portable Service CV of verified commendations, strengths and training — so great service finally has a record." },
  { icon: TrendingUp, title: "Service → commercial impact", body: "Benchmark locations and regions and connect service behaviours to repeat visits, basket size and margin using your own data." },
];

const FAIR = [
  "Anonymous by default — no customer account, no app, under 30 seconds",
  "POPIA-aligned: data minimisation, purpose limitation, retention controls",
  "Ratings never set salaries automatically — they trigger a fair review",
  "Anti-bias & anti-gaming controls with full audit trail",
];

const SECTORS = [
  { icon: Store, title: "Retail", body: "Give every floor, till and fitting-room employee a staff-linked QR lanyard. See which stores, shifts and people lift customer experience, and where the service gaps are." },
  { icon: UtensilsCrossed, title: "Restaurants & hospitality", body: "Let guests rate their waiter or host in seconds at the table. Recover unhappy guests before they leave, and turn great service into public reviews." },
];

const FAQ = [
  { q: "What is Servantage?", a: "Servantage is an employee QR code service rating platform and frontline service intelligence software. Each employee gets a staff-linked QR lanyard or NFC tag so customers can rate the person who served them in under 30 seconds, with no app or login." },
  { q: "How does closed-loop service recovery work?", a: "Low ratings automatically open a service-recovery case for the manager to follow up, while high ratings invite the customer to leave a public review. Every case is tracked from open to resolved." },
  { q: "Who is Servantage for?", a: "Retail stores, restaurants and any business with frontline employees who serve customers face to face, from a single outlet to multi-region groups." },
  { q: "What analytics does Servantage provide?", a: "Employee performance analytics, outlet and executive scorecards, service gap analysis by touchpoint, trust-checked rating trends, NFC vs QR channel splits and AI-generated coaching insights." },
];

export default function Landing() {
  usePageMeta("Servantage | Employee QR Code Service Rating & Frontline Service Intelligence Platform", "/");
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* HERO */}
      <section className="mx-auto max-w-6xl px-5 pb-14 pt-10 sm:px-6 sm:pb-20 sm:pt-14 md:pt-24">
        <div className="max-w-3xl sv-rise">
          <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-indigo-700">
            <Sparkles className="h-3.5 w-3.5" /> Employee QR code service rating platform
          </span>
          <h1 className="mt-5 font-head text-[2rem] font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Make service excellence <span className="text-[#4338CA]">measurable</span>.
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-600 sm:text-lg">
            Servantage is frontline service intelligence and customer feedback software. Staff-linked QR lanyards turn every
            interaction into verified service data — so you can recognise your best people, coach where it counts, and connect great service to real commercial results.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" className="w-full sm:w-auto">
              <Button size="lg" data-testid="hero-get-started" className="w-full bg-[#4338CA] px-7 hover:bg-[#3730A3] sm:w-auto">
                Create your free workspace <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> No credit card</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Demo data included</span>
            <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> POPIA-aligned</span>
          </p>
        </div>
      </section>

      {/* RETAIL IMAGE BREAK */}
      <section className="mx-auto max-w-6xl px-5 pb-12 sm:px-6 sm:pb-16" data-testid="retail-image-break">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-sm sm:rounded-3xl">
          <img
            src="/images/retail-service.webp"
            alt="Retail employee with a staff-linked QR lanyard helping a customer at the counter"
            width="1600" height="900"
            className="h-[220px] w-full object-cover object-center sm:h-[380px] md:h-[440px]"
            loading="lazy" decoding="async"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E1B4B]/80 via-[#1E1B4B]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 p-5 sm:p-10">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white backdrop-blur">
              <Star className="h-3.5 w-3.5" /> The moment that defines your brand
            </span>
            <p className="mt-4 max-w-lg font-head text-lg font-semibold leading-snug text-white sm:text-2xl">
              Every great interaction at the counter is where loyalty is won — Servantage makes it count.
            </p>
          </div>
        </div>
      </section>

      {/* PROOF BAND — service excellence pays */}
      <section className="border-y border-slate-200 bg-[#1E1B4B] py-12 text-white sm:py-16">
        <div className="mx-auto max-w-6xl px-5 sm:px-6">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-300">Why service excellence pays</span>
            <h2 className="mt-3 font-head text-2xl font-bold tracking-tight sm:text-3xl">The commercial case is well established.</h2>
            <p className="mt-2 text-indigo-200">Independent research consistently links superior service to loyalty, referrals and revenue.</p>
          </div>
          <div className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4">
            {PROOF.map((p) => (
              <div key={p.src + p.stat} className="rounded-xl border border-indigo-800/60 bg-indigo-900/40 p-6">
                <div className="sv-metric text-4xl font-bold text-white">{p.stat}</div>
                <p className="mt-3 text-sm leading-relaxed text-indigo-100">{p.body}</p>
                <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-indigo-400">{p.src}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 max-w-3xl text-sm text-indigo-300">
            Servantage doesn't promise a generic uplift. It identifies which service behaviours are statistically
            associated with higher repeat visits, basket size and margin — in <span className="font-semibold text-indigo-100">your own</span> outlets.
          </p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-6xl px-5 py-14 sm:px-6 sm:py-20">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Closed-loop service recovery &amp; employee performance analytics</span>
          <h2 className="mt-3 font-head text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">From the lanyard to the boardroom.</h2>
          <p className="mt-2 text-slate-600">Identity, feedback, trust, measurement, action and recognition — one connected system.</p>
        </div>
        <div className="mt-10 grid gap-4 sm:mt-12 sm:gap-5 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-6 transition-transform hover:-translate-y-1">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
                <f.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-head text-lg font-semibold text-slate-900">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FLOW */}
      <section className="border-t border-slate-200 bg-[#F8FAFC] py-14 sm:py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-6">
          <h2 className="font-head text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">A 30-second moment of truth</h2>
          <div className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 md:grid-cols-4">
            {[
              { icon: QrCode, t: "Scan", d: "Customer scans the employee's QR — no app, no login." },
              { icon: Star, t: "Rate", d: "A quick star rating plus a few service touchpoints." },
              { icon: LifeBuoy, t: "Recover or recognise", d: "Low scores open recovery; high scores invite a review." },
              { icon: LineChart, t: "Improve", d: "Managers coach to the gaps; employees build a Service CV." },
            ].map((s, i) => (
              <div key={s.t} className="relative rounded-xl border border-slate-200 bg-white p-6">
                <span className="absolute right-4 top-4 font-head text-3xl font-bold text-slate-100">{i + 1}</span>
                <s.icon className="h-6 w-6 text-indigo-600" />
                <h3 className="mt-3 font-head font-semibold text-slate-900">{s.t}</h3>
                <p className="mt-1 text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="border-t border-slate-200 py-14 sm:py-20" data-testid="landing-sectors">
        <div className="mx-auto max-w-6xl px-5 sm:px-6">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">Who it's for</span>
            <h2 className="mt-3 font-head text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Frontline employee service identity &amp; gap analysis for retail and restaurants</h2>
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {SECTORS.map((s) => (
              <div key={s.title} className="rounded-xl border border-slate-200 bg-white p-6">
                <s.icon className="h-6 w-6 text-indigo-600" />
                <h3 className="mt-3 font-head text-lg font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAIRNESS */}
      <section className="mx-auto max-w-6xl px-5 py-14 sm:px-6 sm:py-20">
        <div className="grid items-center gap-8 lg:grid-cols-2 lg:gap-10">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-indigo-600">
              <Scale className="h-4 w-4" /> Constructive by design
            </span>
            <h2 className="mt-3 font-head text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
              Built to lift people up — never to punish them.
            </h2>
            <p className="mt-3 text-slate-600">
              A score from 5 reviews is never treated like a score from 500. Ratings start a fair conversation,
              not an automatic penalty — with anti-bias, anti-gaming and dispute controls throughout.
            </p>
          </div>
          <div className="space-y-3">
            {FAIR.map((f) => (
              <div key={f} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500" />
                <span className="text-sm text-slate-700">{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 pb-16 sm:px-6 sm:pb-20" data-testid="landing-faq">
        <h2 className="font-head text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Frequently asked questions</h2>
        <div className="mt-6 divide-y divide-slate-200 rounded-xl border border-slate-200">
          {FAQ.map((f, i) => (
            <details key={f.q} className="group p-5" data-testid={`faq-${i}`}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-head font-semibold text-slate-900">
                <h3 className="text-base">{f.q}</h3>
                <ChevronDown className="h-4 w-4 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-6xl px-5 pb-16 sm:px-6 sm:pb-24">
        <div className="relative overflow-hidden rounded-2xl bg-[#4338CA] p-7 text-white sm:rounded-3xl sm:p-10 md:p-16">
          <div className="relative z-10 max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-indigo-200">
              <Users className="h-4 w-4" /> Your service. Your reputation. Your results.
            </span>
            <h2 className="mt-4 font-head text-2xl font-bold leading-tight tracking-tight sm:text-3xl md:text-4xl">
              Start measuring service excellence today.
            </h2>
            <p className="mt-3 max-w-xl text-indigo-100">
              Spin up a workspace in under a minute. It comes pre-loaded with a live demo so you can see the
              dashboards, Service CV and feedback flow immediately.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link to="/register" className="w-full sm:w-auto">
                <Button size="lg" data-testid="cta-get-started" className="w-full bg-white px-7 text-indigo-900 hover:bg-indigo-50 sm:w-auto">
                  Create your free workspace <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto">
                <Button size="lg" variant="outline" data-testid="cta-signin" className="w-full border-indigo-300 bg-transparent text-white hover:bg-indigo-500/30 hover:text-white sm:w-auto">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
          <div className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 rounded-full bg-indigo-500/30 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 right-24 h-64 w-64 rounded-full bg-indigo-400/20 blur-2xl" />
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
